import { motion } from 'framer-motion';
import { Award, Clock, Users, MapPin, Quote } from 'lucide-react';
import { useProposalData } from '../../context';
import { Section, SectionHeader } from '../layout';

const stats = [
  { label: 'Years in Business', value: '55+', icon: Clock },
  { label: 'Customers Served', value: '10,000+', icon: Users },
  { label: 'Service Locations', value: '12', icon: MapPin },
  { label: 'Industry Awards', value: '25+', icon: Award },
];

const certifications = [
  'Mitsubishi Forklift Certified Dealer',
  'Hyundai Forklift Dealer',
  'Trackmobile Authorized Dealer',
  'OSHA Compliant Training Provider',
];

export function AboutSection() {
  const proposal = useProposalData();

  if (!proposal.sections.showAbout) {
    return null;
  }

  const showTestimonials = proposal.sections.showTestimonials && proposal.testimonials && proposal.testimonials.length > 0;

  return (
    <Section id="about" dark>
      <SectionHeader
        title="ABOUT HERC-U-LIFT"
        subtitle="Our Company"
        dark
      />

      <div className="grid lg:grid-cols-2 gap-12 items-center">
        {/* Company Info */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="font-display text-3xl text-white tracking-wide mb-6">
            YOUR TRUSTED EQUIPMENT PARTNER
          </h3>

          <p className="text-gray-300 leading-relaxed mb-6">
            For over five decades, Herc-U-Lift has been the Upper Midwest's premier
            provider of material handling equipment and solutions. From forklifts
            to aerial work platforms, we deliver the equipment, service, and
            expertise your operation demands.
          </p>

          <p className="text-gray-300 leading-relaxed mb-8">
            Our commitment goes beyond equipment sales. We're partners in your
            success, providing comprehensive maintenance programs, operator
            training, and 24/7 support to keep your operation running at peak
            efficiency.
          </p>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  className="bg-white/5 rounded-xl p-4 text-center border border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Icon className="w-6 h-6 text-hul-red mx-auto mb-2" />
                  <div className="font-display text-2xl text-white">{stat.value}</div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider">
                    {stat.label}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Certifications & Brands */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
            <h4 className="font-heading text-xl uppercase tracking-wider text-hul-red mb-6">
              Certifications & Partnerships
            </h4>

            <div className="space-y-4 mb-8">
              {certifications.map((cert, index) => (
                <motion.div
                  key={index}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <div className="w-2 h-2 bg-hul-red rounded-full" />
                  <span className="text-gray-300">{cert}</span>
                </motion.div>
              ))}
            </div>

            {/* Brands */}
            <div className="border-t border-white/10 pt-6">
              <div className="text-sm text-gray-400 uppercase tracking-wider mb-4">
                Authorized Dealer For
              </div>
              <div className="flex flex-wrap gap-4">
                {['Mitsubishi', 'Hyundai', 'Trackmobile', 'Big Joe'].map((brand) => (
                  <span
                    key={brand}
                    className="px-4 py-2 bg-white/10 rounded-lg text-white font-heading tracking-wider"
                  >
                    {brand}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Testimonials */}
      {showTestimonials && (
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-0.5 bg-hul-red" />
            <span className="font-mono text-sm uppercase tracking-wider text-white/70">
              What Our Customers Say
            </span>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {proposal.testimonials!.map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-6 relative"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                {/* Quote Icon */}
                <div className="absolute -top-3 -left-3 w-10 h-10 bg-hul-red rounded-lg flex items-center justify-center shadow-lg">
                  <Quote className="w-5 h-5 text-white" />
                </div>

                <p className="text-gray-700 leading-relaxed mb-6 pt-4">
                  "{testimonial.quote}"
                </p>

                <div className="flex items-center gap-3">
                  {testimonial.image ? (
                    <img
                      src={testimonial.image}
                      alt={testimonial.author}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-hul-navy flex items-center justify-center">
                      <span className="font-display text-lg text-white">
                        {testimonial.author.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <div className="font-heading text-hul-navy">{testimonial.author}</div>
                    <div className="text-sm text-gray-500">{testimonial.company}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </Section>
  );
}
